import {useQuery} from '@tanstack/react-query';
import {AxiosRequestConfig, AxiosResponse} from 'axios';
import {IApiPaginationResponse} from '../../dto/API.dto';
import {IMediaTrack} from '../../dto/MediaTrack.dto';
import axiosInstance, {apiGet} from '../../axiosConfig';

const getMediaTracks = (
  companyId: number,
  customLibraryId: number,
  parms: ICustomLibraryMediaTracksQueryParms,
) => {
  return axiosInstance
    .get(`v1/company/${companyId}/custom-library/${customLibraryId}/track`, {
      params: parms,
    })
    .then((response: AxiosResponse<IApiPaginationResponse<IMediaTrack[]>>) => {
      return response.data;
    });
};

export type ICustomLibraryMediaTracksQueryParms = {
  search?: string;
  page?: number;
  per_page: number;
};

export const useRqCustomLibraryPaginateMediaTracks = (
  companyId: number = 0,
  customLibraryId: number,
  query: ICustomLibraryMediaTracksQueryParms = {per_page: 10},
  enabled: boolean = true,
) => {
  const params = {...query, page: query.page ? query.page + 1 : 1};
  return useQuery({
    queryKey: [
      `v1/company/${companyId}/custom-library/${customLibraryId}/track`,
      params,
    ],
    queryFn: () => getMediaTracks(companyId, customLibraryId, params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};
export const apiPathGetCustomClips = (company_id: number) => {
  return `/v1/company/${company_id}/on-demand-playlist/create-clip`;
};

export const apiPathGetCustomClipsByCompany = (company_id: number) => {
  return `/v1/company/${company_id}/on-demand-playlist/create-clip/showByCompany`;
};

const apiGetCustomClipsByCompany = async (
  company_id: number,
  parms: ICustomLibraryMediaTracksQueryParms = {per_page: 10},
  pagination: boolean,
) => {
  const config: AxiosRequestConfig = {
    params: {
      ...parms,
      pagination,
    },
  };
  const response = await apiGet(
    apiPathGetCustomClipsByCompany(company_id),
    config,
  );
  return response.data;
};

export const useRqGetCustomClipsByCompany = (
  company_id: number,
  parms: ICustomLibraryMediaTracksQueryParms = {per_page: 10},
  pagination: boolean = true,
  enabled: boolean = true,
) => {
  const params = {...parms, page: parms.page ? parms.page + 1 : 1};
  return useQuery({
    queryKey: [apiPathGetCustomClipsByCompany(company_id), params],
    queryFn: () => apiGetCustomClipsByCompany(company_id, params, pagination),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};

const apiGetCustomClips = async (
  company_id: number,
  stations: number[],
  parms: ICustomLibraryMediaTracksQueryParms = {per_page: 10},
  pagination: boolean,
) => {
  const config: AxiosRequestConfig = {
    params: {
      ...parms,
      pagination,
      stations: stations,
    },
  };
  const response = await apiGet(apiPathGetCustomClips(company_id), config);
  return response.data;
};
export const useRqGetCustomClips = (
  company_id: number,
  stations: number[],
  parms: ICustomLibraryMediaTracksQueryParms = {per_page: 10},
  pagination: boolean = true,
  enabled: boolean = true,
) => {
  const params = {...parms, page: parms.page ? parms.page + 1 : 1};
  return useQuery({
    queryKey: [apiPathGetCustomClips(company_id), params],
    queryFn: () => apiGetCustomClips(company_id, stations, params, pagination),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};

export const apiPathCustomLibraryAll = (companyId: number) => {
  return `v1/company/${companyId}/custom-library/tracks`;
};

const postMediaTracksByLibraryIds = (
  companyId: number,
  body: ICustomLibraryMediaTracksPostBody,
) => {
  return axiosInstance
    .post(apiPathCustomLibraryAll(companyId), body)
    .then((response: AxiosResponse<IApiPaginationResponse<IMediaTrack[]>>) => {
      return response.data;
    });
};

export type ICustomLibraryMediaTracksPostBody = {
  customLibraryIds: number[];
  stationIds?: number[];
  search?: string;
  page?: number;
  per_page: number;
};

export const useRqPostCustomLibraryTracks = (
  companyId: number,
  body: ICustomLibraryMediaTracksPostBody,
  enabled: boolean = true,
) => {
  const params = {...body, page: body.page !== undefined ? body.page + 1 : 1};
  return useQuery({
    queryKey: [apiPathCustomLibraryAll(companyId), params],
    queryFn: () => postMediaTracksByLibraryIds(companyId, params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

// all messages tracks.
export const apiPathGetAllMessageByCompany = (company_id: number) => {
  return `/v1/company/${company_id}/all-messages`;
};

const apiGetAllMessageByCompany = async (
  company_id: number,
  station_id: number,
  parms: ICustomLibraryMediaTracksQueryParms = {per_page: 10},
) => {
  const config: AxiosRequestConfig = {
    params: {
      ...parms,
      station_id,
    },
  };
  const response = await apiGet(
    apiPathGetAllMessageByCompany(company_id),
    config,
  );
  return response.data;
};

export const useRqGetAllMessageByCompany = (
  company_id: number,
  station_id: number,
  parms: ICustomLibraryMediaTracksQueryParms = {per_page: 10},
  enabled: boolean = true,
) => {
  const params = {...parms, page: parms.page ? parms.page + 1 : 1};
  return useQuery({
    queryKey: [apiPathGetAllMessageByCompany(company_id), params],
    queryFn: () => apiGetAllMessageByCompany(company_id, station_id, params),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};
